"use client";

import React, { useCallback, useMemo, useRef, useState } from "react";
import { NumericFormat } from "react-number-format";
import {
  useController,
  useFormContext,
  type Control,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/components/ui/select";
import { Checkbox } from "@/app/components/ui/checkbox";
import { InputWithLabel } from "@/app/components/ui/input";
import ValidationPopover, {
  type ValidationErrorT,
  type ValidationPopoverBgT,
} from "@/app/components/ui/validation-popover";
import { cn } from "@/lib/utils";

const contactControlClass =
  "w-full rounded-md border border-border bg-background px-4 text-[16px] shadow-xs outline-none transition-[color,box-shadow] placeholder:text-muted-foreground focus:border-border focus:ring-0 focus:outline-none focus-visible:border-border focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50 lg:text-sm";

const contactInputClass = cn(
  contactControlClass,
  "py-3 placeholder:text-[16px] lg:placeholder:text-sm"
);

const contactTextareaClass = cn(contactControlClass, "min-h-32 resize-y py-3");

const contactLabelClass = "pb-0.5 pl-1";

const contactScaledInputContainerClass =
  "max-lg:h-11 max-lg:overflow-hidden sm:max-lg:h-12";

const contactScaledInputClass =
  "max-lg:h-[3.142857rem] max-lg:w-[114.285714%] max-lg:origin-top-left max-lg:scale-[0.875] max-lg:text-[16px] max-lg:placeholder:text-[16px] sm:max-lg:h-[3.428571rem]";

const contactScaledTextareaContainerClass =
  "max-lg:min-h-32 max-lg:overflow-hidden";

const contactScaledTextareaClass =
  "max-lg:min-h-[9.142857rem] max-lg:w-[114.285714%] max-lg:origin-top-left max-lg:scale-[0.875] max-lg:text-[16px] max-lg:placeholder:text-[16px]";

type ContactControlBaseProps = {
  id?: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  labelClassName?: string;
  className?: string;
  disabled?: boolean;
  readOnly?: boolean;
  bgVariant?: ValidationPopoverBgT;
  onFieldBlur?: (fieldName: string) => void;
};

type ContactTextInputProps<TFieldValues extends FieldValues = FieldValues> =
  ContactControlBaseProps & {
    name: FieldPath<TFieldValues>;
    control?: Control<TFieldValues>;
    type?: React.HTMLInputTypeAttribute;
    autoComplete?: React.HTMLInputAutoCompleteAttribute;
    inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
    unit?: string;
  };

type ContactTextareaInputProps<TFieldValues extends FieldValues = FieldValues> =
  ContactControlBaseProps & {
    name: FieldPath<TFieldValues>;
    control?: Control<TFieldValues>;
    onFocus?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
    onTouchStart?: (event: React.TouchEvent<HTMLTextAreaElement>) => void;
    onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
    onBlur?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
  };

type ContactSelectOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

type ContactSelectProps<TFieldValues extends FieldValues = FieldValues> = {
  name: FieldPath<TFieldValues>;
  control?: Control<TFieldValues>;
  options: ContactSelectOption[];
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  label?: string;
  required?: boolean;
  labelClassName?: string;
  className?: string;
  triggerClassName?: string;
  bgVariant?: ValidationPopoverBgT;
  onFieldBlur?: (fieldName: string) => void;
  onSelectionChange?: (value: string) => void;
};

type ContactCheckboxProps<TFieldValues extends FieldValues = FieldValues> = {
  name: FieldPath<TFieldValues>;
  control?: Control<TFieldValues>;
  onCheckedChange?: (checked: boolean) => void;
  onFieldBlur?: (fieldName: string) => void;
  disabled?: boolean;
  readOnly?: boolean;
  className?: string;
  id?: string;
  bgVariant?: ValidationPopoverBgT;
  children?: React.ReactNode;
};

function useResolvedControl<TFieldValues extends FieldValues>(
  control?: Control<TFieldValues>
) {
  const formContext = useFormContext<TFieldValues>();
  const actualControl = control || formContext?.control;

  if (!actualControl) {
    throw new Error(
      "Contact form control must be used inside FormProvider or with control prop"
    );
  }

  return actualControl;
}

function getErrorMessages(message?: string) {
  return message ? [message] : [];
}

function ContactLabel({
  id,
  label,
  required,
  className,
}: {
  id?: string;
  label?: string;
  required?: boolean;
  className?: string;
}) {
  if (!label) {
    return null;
  }

  return (
    <label htmlFor={id} className={cn(contactLabelClass, className)}>
      {label}
      {required && <span className="ml-1 text-red-500">*</span>}
    </label>
  );
}

export function ContactTextInput<
  TFieldValues extends FieldValues = FieldValues,
>({
  name,
  control,
  onChange,
  onBlur,
  onFieldBlur,
  className,
  labelClassName,
  bgVariant = "muted",
  unit,
  ...props
}: ContactTextInputProps<TFieldValues>) {
  const actualControl = useResolvedControl(control);
  const {
    field,
    fieldState: { error },
  } = useController({ name, control: actualControl });

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      field.onChange(event.target.value);
      onChange?.(event);
    },
    [field, onChange]
  );

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      field.onBlur();
      onBlur?.(event);
      onFieldBlur?.(name);
    },
    [field, name, onBlur, onFieldBlur]
  );

  const setInputRef = useCallback(
    (node: HTMLInputElement | null) => {
      field.ref(node);
    },
    [field]
  );

  return (
    <div className="relative">
      <InputWithLabel
        {...props}
        id={props.id ?? String(name)}
        name={field.name}
        aria-label={props.label}
        value={field.value ?? ""}
        onChange={handleChange}
        onBlur={handleBlur}
        ref={setInputRef}
        error={error?.message}
        bgVariant={bgVariant}
        labelClassName={cn("font-semibold", labelClassName)}
        containerClassName={contactScaledInputContainerClass}
        className={cn(
          contactInputClass,
          className,
          unit && "pr-12",
          contactScaledInputClass
        )}
      />
      {unit && (
        <span className="text-muted-foreground pointer-events-none absolute right-4 bottom-3 text-sm">
          {unit}
        </span>
      )}
    </div>
  );
}

export function ContactTextareaInput<
  TFieldValues extends FieldValues = FieldValues,
>({
  name,
  control,
  onChange,
  onBlur,
  onFieldBlur,
  label,
  required,
  labelClassName,
  className,
  bgVariant = "muted",
  ...props
}: ContactTextareaInputProps<TFieldValues>) {
  const actualControl = useResolvedControl(control);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const {
    field,
    fieldState: { error },
  } = useController({ name, control: actualControl });

  const hasError = Boolean(error?.message);
  const errorMessages = useMemo(
    () => getErrorMessages(error?.message),
    [error?.message]
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      field.onChange(event.target.value);
      onChange?.(event);
    },
    [field, onChange]
  );

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLTextAreaElement>) => {
      field.onBlur();
      onBlur?.(event);
      onFieldBlur?.(name);
    },
    [field, name, onBlur, onFieldBlur]
  );

  const setTextareaRef = useCallback(
    (node: HTMLTextAreaElement | null) => {
      textareaRef.current = node;
      field.ref(node);
    },
    [field]
  );

  return (
    <div className="flex flex-col gap-1.5">
      <ContactLabel
        id={String(name)}
        label={label}
        required={required}
        className={cn("font-semibold", labelClassName)}
      />
      <ValidationPopover
        error={
          hasError && errorMessages.length > 0
            ? (errorMessages as (string | ValidationErrorT)[])
            : undefined
        }
        isOpen={hasError ? popoverOpen : false}
        onOpenChange={setPopoverOpen}
        triggerRef={textareaRef}
        bgVariant={bgVariant}
      >
        <div className={contactScaledTextareaContainerClass}>
          <textarea
            {...props}
            id={String(name)}
            name={field.name}
            value={field.value ?? ""}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={(event) => {
              if (hasError) {
                setPopoverOpen(true);
              }
              props.onFocus?.(event);
            }}
            onTouchStart={(event) => {
              if (hasError) {
                setPopoverOpen(true);
              }
              props.onTouchStart?.(event);
            }}
            ref={setTextareaRef}
            aria-invalid={hasError || undefined}
            className={cn(
              contactTextareaClass,
              hasError && "border-destructive outline-destructive outline",
              className,
              contactScaledTextareaClass
            )}
          />
        </div>
      </ValidationPopover>
    </div>
  );
}

export function ContactSelect<TFieldValues extends FieldValues = FieldValues>({
  name,
  control,
  options,
  placeholder = "Auswählen...",
  label,
  required,
  labelClassName,
  disabled,
  readOnly,
  className,
  triggerClassName,
  bgVariant = "muted",
  onFieldBlur,
  onSelectionChange,
}: ContactSelectProps<TFieldValues>) {
  const actualControl = useResolvedControl(control);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const {
    field,
    fieldState: { error },
  } = useController({ name, control: actualControl });

  const hasError = Boolean(error?.message);
  const errorMessages = useMemo(
    () => getErrorMessages(error?.message),
    [error?.message]
  );

  const normalizedValue =
    field.value === null || field.value === undefined
      ? undefined
      : String(field.value);

  const handleValueChange = useCallback(
    (value: string) => {
      if (readOnly) return;
      field.onChange(value);
      onSelectionChange?.(value);
    },
    [field, onSelectionChange, readOnly]
  );

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (isOpen) return;
      field.onBlur();
      onFieldBlur?.(name);
    },
    [field, name, onFieldBlur]
  );

  const triggerNode = (
    <SelectTrigger
      ref={triggerRef}
      className={cn(
        contactControlClass,
        "data-[placeholder]:text-muted-foreground [&_svg]:text-muted-foreground h-auto min-h-11 cursor-pointer justify-between shadow-none lg:min-h-[46px]",
        readOnly && "bg-muted/30 cursor-default",
        hasError && "border-destructive outline-destructive outline",
        triggerClassName
      )}
      aria-invalid={hasError || undefined}
      aria-label={
        label
          ? normalizedValue
            ? `${label}: ${
                options.find((option) => option.value === normalizedValue)
                  ?.label || normalizedValue
              }`
            : `${label}${placeholder ? `, ${placeholder}` : ""}`
          : placeholder || undefined
      }
    >
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
  );

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <ContactLabel
        id={String(name)}
        label={label}
        required={required}
        className={cn("font-semibold", labelClassName)}
      />
      <Select
        value={normalizedValue}
        onValueChange={handleValueChange}
        onOpenChange={handleOpenChange}
        disabled={disabled || readOnly}
      >
        <ValidationPopover
          error={
            hasError && errorMessages.length > 0
              ? (errorMessages as (string | ValidationErrorT)[])
              : undefined
          }
          bgVariant={bgVariant}
          triggerRef={triggerRef}
          triggerOnHover={hasError && errorMessages.length > 0}
        >
          {triggerNode}
        </ValidationPopover>
        <SelectContent>
          {options.map((option) => (
            <SelectItem
              key={option.value}
              value={option.value}
              disabled={option.disabled || readOnly}
            >
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export function ContactCheckbox<
  TFieldValues extends FieldValues = FieldValues,
>({
  name,
  control,
  onCheckedChange,
  onFieldBlur,
  disabled,
  readOnly,
  className,
  id,
  bgVariant = "muted",
  children,
}: ContactCheckboxProps<TFieldValues>) {
  const actualControl = useResolvedControl(control);
  const {
    field,
    fieldState: { error },
  } = useController({ name, control: actualControl });

  const hasError = Boolean(error?.message);
  const errorMessages = useMemo(
    () => getErrorMessages(error?.message),
    [error?.message]
  );
  const checkboxId = id || String(name);

  const handleCheckedChange = useCallback(
    (checked: boolean) => {
      if (readOnly) return;
      field.onChange(checked);
      onCheckedChange?.(checked);
    },
    [field, onCheckedChange, readOnly]
  );

  const handleBlur = useCallback(() => {
    field.onBlur();
    onFieldBlur?.(name);
  }, [field, name, onFieldBlur]);

  return (
    <ValidationPopover
      error={
        hasError && errorMessages.length > 0
          ? (errorMessages as (string | ValidationErrorT)[])
          : undefined
      }
      bgVariant={bgVariant}
      triggerOnHover={hasError && errorMessages.length > 0}
      className="-translate-x-24"
    >
      <div
        className={cn(
          "inline-flex items-start gap-3",
          hasError && "text-destructive"
        )}
      >
        <Checkbox
          id={checkboxId}
          checked={field.value ?? false}
          onCheckedChange={handleCheckedChange}
          onBlur={handleBlur}
          disabled={disabled || readOnly}
          className={cn(
            "border-border bg-background mt-0.5 size-5 rounded-[4px] shadow-xs",
            hasError && "border-destructive aria-invalid:border-destructive",
            className
          )}
          aria-invalid={hasError || undefined}
        />
        {children && (
          <label htmlFor={checkboxId} className="cursor-pointer pt-0.5">
            {children}
          </label>
        )}
      </div>
    </ValidationPopover>
  );
}

export function ContactPhoneInput<
  TFieldValues extends FieldValues = FieldValues,
>(props: ContactTextInputProps<TFieldValues>) {
  return (
    <ContactTextInput
      placeholder="+491234567890"
      {...props}
      type="tel"
      autoComplete="tel"
      inputMode="tel"
    />
  );
}

export function ContactMileageInput<
  TFieldValues extends FieldValues = FieldValues,
>({
  name,
  control,
  onFieldBlur,
  unit = "km",
  className,
  labelClassName,
  bgVariant = "muted",
  ...props
}: ContactTextInputProps<TFieldValues>) {
  const actualControl = useResolvedControl(control);
  const {
    field,
    fieldState: { error },
  } = useController({ name, control: actualControl });
  const {
    type: numericType,
    onChange: numericOnChange,
    onBlur: numericOnBlur,
    ...numericFormatProps
  } = props;
  void numericType;
  void numericOnChange;
  void numericOnBlur;

  return (
    <div className="relative">
      <NumericFormat
        customInput={InputWithLabel}
        id={props.id ?? String(name)}
        name={field.name}
        aria-label={props.label}
        thousandSeparator="."
        decimalSeparator=","
        decimalScale={0}
        allowNegative={false}
        valueIsNumericString
        value={field.value ?? ""}
        onValueChange={(values) => {
          field.onChange(values.floatValue ?? null);
        }}
        onBlur={() => {
          field.onBlur();
          onFieldBlur?.(name);
        }}
        getInputRef={field.ref}
        error={error?.message}
        bgVariant={bgVariant}
        labelClassName={cn("font-semibold", labelClassName)}
        containerClassName={contactScaledInputContainerClass}
        className={cn(
          contactInputClass,
          className,
          "pr-12",
          contactScaledInputClass
        )}
        {...numericFormatProps}
      />
      {unit && (
        <span className="text-muted-foreground pointer-events-none absolute right-4 bottom-3 text-sm">
          {unit}
        </span>
      )}
    </div>
  );
}
